import { useEffect, useState, type CSSProperties } from "react";
import { toast } from "sonner";
import { Copy, Link2 } from "lucide-react";
import type { Area, ContainerLink } from "@desk/contracts";
import { useApi } from "@/data/context";
import type { ApiClient } from "@/data/api";
import { cn } from "@/lib/utils";

type Details = Pick<Area, "description" | "color" | "links">;

// Media files never change for an id (immutable on the server), so one blob URL per id is
// kept for the life of the page.
const urls = new Map<string, Promise<string>>();
function mediaUrl(api: ApiClient, id: string): Promise<string> {
  let p = urls.get(id);
  if (!p) {
    p = api.mediaBlob(id).then((b) => URL.createObjectURL(b));
    p.catch(() => urls.delete(id));
    urls.set(id, p);
  }
  return p;
}

export function useMediaUrl(id: string | null | undefined): string | null {
  const api = useApi();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    setUrl(null);
    if (!id) return;
    let live = true;
    mediaUrl(api, id).then(
      (u) => live && setUrl(u),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [api, id]);
  return url;
}

/** A header image (e.g. a wordmark) in place of the page title. Decorative: the h1 keeps the name. */
export function HeaderMedia({ mediaId }: { mediaId: string }) {
  const url = useMediaUrl(mediaId);
  return (
    <span className="flex h-12 items-center">
      {url && <img src={url} alt="" draggable={false} className="h-full w-auto max-w-[min(100%,420px)] select-none object-contain object-left" />}
    </span>
  );
}

/** "file:///Users/me/My%20Repo" -> "/Users/me/My Repo". */
export function filePath(url: string): string {
  try {
    return decodeURIComponent(new URL(url).pathname);
  } catch {
    return url;
  }
}

const chip =
  "inline-flex h-7 max-w-[260px] items-center gap-1.5 rounded-md border border-border/80 px-2 text-[12.5px] text-foreground/80 outline-none transition-colors hover:border-[var(--container-accent,var(--border))] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40";

function LinkChip({ link }: { link: ContainerLink }) {
  if (link.url.toLowerCase().startsWith("file:")) {
    const path = filePath(link.url);
    return (
      <button
        type="button"
        className={chip}
        title={path}
        aria-label={`Copy path for ${link.label}: ${path}`}
        onClick={() =>
          void navigator.clipboard.writeText(path).then(
            () => toast("Copied path", { description: path }),
            () => toast.error("Couldn't copy the path"),
          )
        }
      >
        <Copy className="size-3.5 shrink-0 text-subtle" aria-hidden />
        <span className="truncate">{link.label}</span>
      </button>
    );
  }
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={chip}
      title={link.url}
      onClick={(e) => {
        if (!window.deskBridge) return;
        e.preventDefault();
        void window.deskBridge.openExternal(link.url);
      }}
    >
      <Link2 className="size-3.5 shrink-0 text-subtle" aria-hidden />
      <span className="truncate">{link.label}</span>
    </a>
  );
}

/** Description and reference links under an area or space title. Renders nothing when both are empty. */
export function ContainerDetails({ details, className }: { details: Details; className?: string }) {
  if (!details.description && !details.links.length) return null;
  return (
    <div className={cn("flex flex-col gap-2.5", className)} style={details.color ? ({ "--container-accent": details.color } as CSSProperties) : undefined}>
      {details.description && <p className="max-w-[640px] text-[14px] leading-relaxed text-muted-foreground">{details.description}</p>}
      {details.links.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Links">
          {details.links.map((l, i) => (
            <li key={`${i}-${l.url}`}>
              <LinkChip link={l} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
